/* eslint-disable monorepo/no-internal-import */
import {useQuery} from '@aboutyou/data-fetcher/src/react-query';
import {
  exists,
  localStorageGetParsedItem,
  localStorageRemoveItem,
  localStorageSetItem,
} from '@aboutyou/utils';
import {useCallback, useEffect} from 'react';
import {getIsConsentBannerAccepted} from '../utils/consentBannerAcceptedState';
import {getValueToPersist} from '../utils/getValueToPersist';

type StoredValue<Value> = {
  value?: Value | undefined;
  valueAfterCookieConsent?: Value | undefined;
};

interface LocalStorageState<Value> {
  key: string;
  value: StoredValue<Value>;
}

const getLocalStorageKey = (key: string) => `@aboutyou/useLocalStorage/${key}`;
const EMPTY_VALUE = Symbol('Empty_value');
/**
 * This hook provides updates for the given localStorage key in all the component it is used.
 * This hook is deprecated because it has performance issues when overused + it leads to hydration issues when server rendered. `useLocalStorage` is the recommended alternative.
 */
export function useLocalStorage_DEPRECATED<Value>({
  key,
  defaultValue,
  fallbackValue = defaultValue,
  ignoreUpdatesOutsideCurrentTab = false,
  forcePersistValue_ILLEGALLY = false,
}: {
  key: string;
  /** Default value is used when no value exists in browser's local storage */
  defaultValue: Value;
  /** Fallback value is used before hydration is done and on the server */
  fallbackValue?: Value;
  /** Ignore updates to localStorage outside current tab. eg: in another tab or window */
  ignoreUpdatesOutsideCurrentTab?: boolean;
  /** Setting this to true will force save the value in localStorage even when there is no cookie consent for doing so.
   * This is in general a legal threat and can only be used if agreed with the legal department.
   * It will be mandatory to adjust the consent banner text for all countries before the change can go online.
   * Please use this only if it's strictly mandatory to make the ABOUT YOU website work
   */
  forcePersistValue_ILLEGALLY?: boolean;
}) {
  const fallbackReturn = [
    fallbackValue,
    (_newValue: Value | undefined) => {},
  ] as const;
  if (!process.browser) {
    return fallbackReturn;
  }

  /* eslint-disable react-hooks/rules-of-hooks */
  const localStorageRequest = useLocalStorageRequest<Value>({
    key,
    defaultValue,
    forcePersistValue_ILLEGALLY,
  });

  const setLocalStorage = useCallback(
    async (nextValue: Value | undefined) => {
      localStorageRequest.updateCache(currentState => {
        const canPersist =
          getIsConsentBannerAccepted() || forcePersistValue_ILLEGALLY;

        const resolvedValue =
          nextValue === undefined ? (EMPTY_VALUE as Value) : nextValue;

        const valueToStore: StoredValue<Value> = {
          value: canPersist ? currentState?.value.value : resolvedValue,
          valueAfterCookieConsent: canPersist ? resolvedValue : undefined,
        };

        if (
          valueToStore.value === currentState?.value.value &&
          valueToStore.valueAfterCookieConsent ===
            currentState?.value.valueAfterCookieConsent
        ) {
          return {
            key,
            value: currentState?.value ?? valueToStore,
          };
        }

        return {
          key,
          value: valueToStore,
        };
      });
    },
    [forcePersistValue_ILLEGALLY, key, localStorageRequest],
  );

  const resolvedValue =
    localStorageRequest.data?.value?.valueAfterCookieConsent ??
    localStorageRequest.data?.value?.value;

  const value: Value | undefined =
    resolvedValue === EMPTY_VALUE ? undefined : resolvedValue ?? defaultValue;

  useEffect(
    function copyLocalStorageToState() {
      const listener = ({key: eventKey}: StorageEvent) => {
        if (eventKey !== key) {
          return;
        }

        const localStorageValue: Value = localStorageGetParsedItem(eventKey);

        // Comparing JSON strings prevents infinite loops where the hook is used in two components simultaneously
        if (JSON.stringify(value) !== JSON.stringify(localStorageValue)) {
          // eslint-disable-next-line local-rules/set-state-in-use-effect -- TODO: Cleanup setState in useEffect https://aboutyou.atlassian.net/browse/ASFE-7602
          setLocalStorage(localStorageValue);
        }
      };

      if (!ignoreUpdatesOutsideCurrentTab) {
        window.addEventListener('storage', listener);
      }

      return () => {
        if (!ignoreUpdatesOutsideCurrentTab) {
          window.removeEventListener('storage', listener);
        }
      };
    },
    [ignoreUpdatesOutsideCurrentTab, key, setLocalStorage, value],
  );

  return [value, setLocalStorage] as const;
}

const useLocalStorageRequest = <Value,>({
  key,
  defaultValue,
  forcePersistValue_ILLEGALLY,
}: {
  key: string;
  defaultValue: Value;
  forcePersistValue_ILLEGALLY?: boolean;
}) => {
  const isCookieConsentAccepted = getIsConsentBannerAccepted();
  const localStorageValue: Value =
    isCookieConsentAccepted || forcePersistValue_ILLEGALLY
      ? localStorageGetParsedItem(key) ?? defaultValue
      : defaultValue;

  const initialData = {
    key,
    value: {
      value: localStorageValue,
      valueAfterCookieConsent: localStorageValue,
    },
  };

  const localStorageRequest = useQuery<LocalStorageState<Value>>(
    getLocalStorageKey(key),
    async (): Promise<LocalStorageState<Value>> => {
      return initialData;
    },
    {
      initialData,
      onSuccess: (nextState: LocalStorageState<Value>) => {
        const nextValue =
          nextState.value?.valueAfterCookieConsent ?? nextState.value?.value;
        if (nextValue === EMPTY_VALUE) {
          localStorageRemoveItem(nextState.key);
          return;
        }
        const canPersist =
          getIsConsentBannerAccepted() || forcePersistValue_ILLEGALLY;

        if (canPersist) {
          if (
            !exists(nextValue) ||
            localStorageGetParsedItem(key) === nextValue
          ) {
            return;
          }
          localStorageSetItem(
            key,
            getValueToPersist({
              valueBeforeCookieConsent: nextState.value?.value,
              valueAfterCookieConsent: nextState.value.valueAfterCookieConsent,
            }),
          );
        }
      },
    },
  );

  return localStorageRequest;
};
